import "../custom-elements/product-media"
import EVENTS from "../lib/events"
import { dialog } from "../lib/modals"

class MainProductDefault extends HTMLElement {
  constructor() {
    super()

    this.selectors = {
      productInner: ".product-inner",

      // Set sticky container
      detailsContainer: ".product__details",
      mediaContainer: ".product__media",

      // Variant change events
      buyButtonBlock: "product-block-buy-buttons",
      buyButtons: "[js-buy-buttons]",
      buyButtonDialog: ".buy-button-dialog",
      inventoryStatusBlock: "[js-product-block-inventory-status]",
      mediaBlock: "product-media",
      mediaItem: ".media",
      popupModalDialog: ".product-block-variant-selector--popup-modal",
      priceBlock: "[js-product-block-price]",
      productForm: "[data-product-form]",
      productFormIdInput: "input[name='id']",
      skuBlock: "[js-product-block-sku]",
      stickyAddToCartContent: "[js-sticky-atc-content]",
      stickyAddToCartDrawer: "[data-fluco-dialog='sticky-add-to-cart-modal']",
      storeAvailabilityContainer: "[js-store-availability-container]",
      variantBlock: "product-block-variant-selector",
      variantMediaGrouping: "[data-enable-media-grouping='true']",
      variantSelectedInputs: "div input:checked",
      selectVariantById: (id) =>
        `[js-variant-input][data-option-value-id="${id}"]`,
    }

    this.classes = {
      changePending: "change-pending",
      hasError: "has-error",
      modalWillOpen: "modal--will-open",
      stickyDetailsContainer: "sticky-details-container",
      stickyMediaContainer: "sticky-media-container",
      preventAnimation: "prevent-animation",
      quickViewProduct: "quick-view-product",
    }
  }

  connectedCallback() {
    this.controller = new AbortController()
    this.sectionId = this.getAttribute("data-section-id")
    this.isFeatured =
      this.getAttribute("data-section-type") === "featured-product"
    this.sectionType = this.getAttribute("data-section-type")
    this.productURL = this.getAttribute("data-product-url")
    this.productInner = this.querySelector(this.selectors.productInner)
    this.mediaContainer = this.querySelector(this.selectors.mediaContainer)
    this.detailsContainer = this.querySelector(this.selectors.detailsContainer)

    // Sticky container
    this.setStickyContainer()

    // Variant change handling selectors
    this.productForm = this.querySelector(this.selectors.productForm)
    this.buyButtonBlock = this.querySelector(this.selectors.buyButtonBlock)
    this.mediaBlock = this.querySelector(this.selectors.mediaBlock)
    this.variantBlock = this.querySelector(this.selectors.variantBlock)
    this.variantMediaGrouping = this.querySelector(
      this.selectors.variantMediaGrouping,
    )

    // Listen for variant change
    this.addEventListener(
      EVENTS.PRODUCT_VARIANT_CHANGING,
      (event) => {
        this.handleNewVariant(event.detail)
      },
      { signal: this.controller.signal },
    )

    // When a product is added to cart from quick view, close quick view
    if (this.sectionType === this.classes.quickViewProduct) {
      this.addEventListener(
        EVENTS.PRODUCT_ADD_SUCCESS,
        () => {
          window.dispatchEvent(
            new CustomEvent(
              EVENTS.MODAL_POPUP_CLOSE(
                `quick-view-${this.getAttribute("data-product-id")}`,
              ),
            ),
          )
        },
        { signal: this.controller.signal },
      )
    }
  }

  //
  // General setup functions
  //
  setStickyContainer() {
    this.stickyContainer = this.productInner.getAttribute(
      "data-sticky-effect-container",
    )
    this.galleryLayout = this.productInner.getAttribute("data-gallery-layout")

    if (
      this.stickyContainer === "gallery" &&
      (this.galleryLayout === "thumbnails_below" ||
        this.galleryLayout === "thumbnails_left")
    ) {
      this.mediaContainer.classList.add(this.classes.stickyMediaContainer)
      this.detailsContainer.classList.remove(
        this.classes.stickyDetailsContainer,
      )
    }

    if (this.stickyContainer === "product_details") {
      this.detailsContainer.classList.add(this.classes.stickyDetailsContainer)
      this.mediaContainer.classList.remove(this.classes.stickyMediaContainer)
    }
  }

  //
  // Variant on change functions
  //
  handleNewVariant(newElements) {
    this.detailsContainer.classList.add(this.classes.changePending)

    const newOptionInput = newElements.newOptionInput
    const newVariantId = newOptionInput.getAttribute("data-variant-id")

    const params = new URLSearchParams({
      section_id: this.sectionId,
    })

    // Set params with new variant for fetch
    if (newVariantId) {
      params.set("variant", newVariantId)
    } else {
      // Build params from currently selected variants
      const selectedOptionValues = Array.from(
        this.querySelectorAll(this.selectors.variantSelectedInputs),
      )

        .map((element) => element.getAttribute("data-option-value-id"))
        .join(",")

      params.set("option_values", selectedOptionValues)
    }

    // URL only provided if connected to Shopify sibling products
    const newProductUrl = newOptionInput.getAttribute("data-product-url")

    if (newProductUrl) this.productURL = newProductUrl

    if (!newVariantId && !newProductUrl) {
      this.setUnavailable()
      return
    }

    // Fetch new html to repaint applicable blocks
    fetch(`${this.productURL}?${params}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html")

        // If we're on the product page, update the URL for history and deep linking
        if (this.sectionType === "product") {
          this.updateUrlWithVariant(newVariantId)
        }

        // Update html in product blocks
        const blockContentToReplace = [
          "variantBlock",
          "skuBlock",
          "priceBlock",
          "buyButtons",
          "inventoryStatusBlock",
          "storeAvailabilityContainer",
          "stickyAddToCartContent",
        ]

        // Deregister dialogs before updating HTML so they can be reset with new content
        // The availability is removed in quick view, so it's possible that this doesn't exist
        // This should be blocked when updating variants within the Quick view so it doesn't wipe the PDP dialogs
        if (
          this.buyButtonBlock &&
          this.sectionType !== this.classes.quickViewProduct
        ) {
          const dialogKeysToDestroy = [
            `store-availability-drawer-${this.sectionId}`,
            "sticky-add-to-cart-modal",
          ]

          for (const key of dialogKeysToDestroy) {
            if (window.flu.dialogs[key]) {
              window.flu.dialogs[key][1].destroy({ deregister: true })
            }
          }
        }

        this.updateBlockHTML(blockContentToReplace, html)

        // Update product form's variant
        const htmlVariantId = html
          .querySelector(this.selectors.productForm)
          .getAttribute("data-current-product-id")

        // When we're in quick view, the product form is loaded after this component connects
        // and assigns this, so we need to re-assign it
        if (!this.productForm) {
          this.productForm = this.querySelector(this.selectors.productForm)
        }

        this.productForm.setAttribute("data-current-product-id", htmlVariantId)
        this.productForm
          .querySelector(this.selectors.productFormIdInput)
          .setAttribute("value", htmlVariantId)

        //
        // Interactive content requiring events for repainting listeners
        //
        if (this.buyButtonBlock) {
          const newOptionId = newOptionInput.getAttribute(
            "data-option-value-id",
          )

          this.updateBuyButtonBlockContent(
            newElements.withinStickyModal,
            newOptionId,
          )
        }

        if (this.variantBlock) {
          this.updateVariantSelectorBlockContent()
        }

        let hasMediaGrouping = false
        let newContent = newOptionInput
        if (this.variantMediaGrouping) {
          hasMediaGrouping = true
          newContent = html.querySelector(this.selectors.mediaBlock)
        }

        this.updateMediaBlockContent(newContent, hasMediaGrouping)
        this.detailsContainer.classList.remove(this.classes.changePending)
      })
  }

  getUrlWithVariant(url, id) {
    const parsedURL = new URL(url)
    const urlBase = parsedURL.origin + parsedURL.pathname
    const params = parsedURL.searchParams

    params.set("variant", id)

    return `${urlBase}?${params}`
  }

  setUnavailable() {
    const unavailableSelectors = [
      this.selectors.priceBlock,
      this.selectors.skuBlock,
    ]

    this.querySelectorAll(unavailableSelectors).forEach(({ classList }) =>
      classList.add("hidden"),
    )
    this.disableSubmitButtons()
    this.detailsContainer.classList.remove(this.classes.changePending)
  }

  disableSubmitButtons() {
    const unavailableString = window.theme.strings.products.product.unavailable
    const buyButtons = this.querySelector(this.selectors.buyButtons)
    const submitButtons = buyButtons.querySelectorAll("button")

    submitButtons.forEach((button) => {
      const submitButtonLabels = button.querySelectorAll(".button__label")

      button.setAttribute("disabled", "disabled")
      submitButtonLabels.forEach((label) => {
        label.textContent = unavailableString
      })
    })
  }

  updateUrlWithVariant(variantId) {
    const url = this.getUrlWithVariant(window.location.href, variantId)
    window.history.replaceState({ path: url }, "", url)
  }

  updateBlockHTML(contentToUpdateArray, html) {
    // TODO: Reassess approach, particularly for SKU where additional element
    // required so the innerHTML can be updated.
    contentToUpdateArray.forEach((block) => {
      this[block] = this.querySelector(this.selectors[block])
      this.htmlBlock = html.querySelector(this.selectors[block])

      if (this[block]) {
        this[block].outerHTML = this.htmlBlock.outerHTML
      }
    })
  }

  //
  // Send updated content via events for blocks to handle
  //

  updateBuyButtonBlockContent(withinStickyModal, lastOptionSelectedId) {
    this.buyButtonBlock.classList.remove(this.classes.hasError)
    this.buyButtonBlock = this.querySelector(this.selectors.buyButtonBlock)

    // Register new dialog content for store availability and sticky atc
    const buyButtonDialogs = this.buyButtonBlock.querySelectorAll(
      this.selectors.buyButtonDialog,
    )
    buyButtonDialogs.forEach((dialogEl) => {
      dialog(dialogEl)
    })

    // Control animations within open Sticky add to cart modal
    this.stickyDialog = this.buyButtonBlock.querySelector(
      this.selectors.stickyAddToCartDrawer,
    )

    if (withinStickyModal && this.stickyDialog) {
      this.stickyDialog.classList.remove(this.classes.modalWillOpen)

      // A race condition requires a time out here otherwise selector will return null
      setTimeout(() => {
        this.stickyDialog
          .querySelector(this.selectors.selectVariantById(lastOptionSelectedId))
          ?.focus()
      }, 0)

      window.dispatchEvent(
        new CustomEvent(EVENTS.MODAL_POPUP_OPEN("sticky-add-to-cart-modal")),
      )
    }

    this.buyButtonBlock.dispatchEvent(
      new CustomEvent(EVENTS.PRODUCT_VARIANT_CHANGED),
    )
  }

  updateVariantSelectorBlockContent() {
    // The Quick view approach within product-item.js removes whitelisted dialogs and
    // injects them after the Quick view dialog to allow for nesting that doesn't conflict
    // (ex. Clicking the close button on a popup doesn't close Quick view). Variant change
    // inserts the dialog back to their original placement and need to be removed again.
    if (this.sectionType === this.classes.quickViewProduct) {
      const defaultPopupModalDialog = this.querySelector(
        this.selectors.popupModalDialog,
      )

      defaultPopupModalDialog?.remove()
    }

    this.variantBlock.dispatchEvent(
      new CustomEvent(EVENTS.PRODUCT_VARIANT_CHANGED),
    )
  }

  updateMediaBlockContent(newContent, hasMediaGrouping) {
    // There's a split path depending on if Media grouping is enabled
    // Media grouping needs to update indicators and repaint the block
    if (hasMediaGrouping) {
      const mediaSize = parseInt(
        newContent.getAttribute("data-media-count"),
        10,
      )

      // TODO: Look into alternate approach in CSS to do the calcs with --mediaCount
      const mobileThumbnailWidthPercentage = (
        100 / parseInt(mediaSize, 10)
      ).toFixed(2)

      this.mediaBlock.style.setProperty(
        "--mobile-thumbnail-width-percentage",
        `${mobileThumbnailWidthPercentage}%`,
      )

      this.mediaBlock = this.querySelector(this.selectors.mediaBlock)
      this.mediaBlock.outerHTML = newContent.outerHTML

      // Reassign media block so it's pulling the most up to date one
      this.mediaBlock = this.querySelector(this.selectors.mediaBlock)
      this.mediaBlock.dispatchEvent(
        new CustomEvent(EVENTS.PRODUCT_VARIANT_CHANGED, {
          detail: {
            hasMediaGrouping: true,
            mediaSize,
          },
        }),
      )
    } else {
      // Media without grouping needs set navigate to featured media, properly
      // trigger slider events, styling and stay aware of other featured media
      const newFeaturedMedia = newContent.getAttribute(
        "data-option-value-featured-media",
      )

      if (newFeaturedMedia) {
        const variantMediaChangedEvent = new CustomEvent(
          EVENTS.PRODUCT_VARIANT_CHANGED,
          {
            bubbles: true,
            detail: {
              hasMediaGrouping: false,
              newFeaturedMedia,
            },
          },
        )

        this.mediaItem = this.querySelector(this.selectors.mediaItem)
        this.mediaItem.dispatchEvent(variantMediaChangedEvent)
      }
    }
  }

  disconnectedCallback() {
    this.controller.abort()
  }
}

if (!customElements.get("main-product-default")) {
  customElements.define("main-product-default", MainProductDefault)
}
